'use client'

import {useState} from 'react'
import {SlidersHorizontal} from 'lucide-react'
import {useIntl} from 'react-intl'
import {Button} from '@/components/ui/button'
import {Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger} from '@/components/ui/sheet'
import {
  ProductFacetSidebar,
  type ProductFacetDefinition,
} from '@/components/products/ProductFacetSidebar'
import {cn} from '@/lib/utils'

type MobileFacetSheetProps<TFilterKey extends string = string> = {
  definitions: ProductFacetDefinition<TFilterKey>[]
  collapsedSections: Record<string, boolean>
  onToggleSection: (key: TFilterKey) => void
  onToggleOption: (key: TFilterKey, value: string) => void
  onClearAll: () => void
  className?: string
}

/**
 * Mobile entry point to the facet filters.
 *
 * The desktop sidebar is hidden below `lg`, which left phones with no way to
 * filter at all (JO2-166). This renders a trigger below `lg` only, and reuses
 * the exact same `ProductFacetSidebar` inside a sheet so the two breakpoints
 * cannot drift apart.
 *
 * The sheet deliberately stays open while facets are toggled — selecting one
 * option should not cost the user the panel they are filtering in.
 */
export function MobileFacetSheet<TFilterKey extends string = string>({
  definitions,
  collapsedSections,
  onToggleSection,
  onToggleOption,
  onClearAll,
  className,
}: MobileFacetSheetProps<TFilterKey>) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const activeCount = definitions.reduce((total, definition) => total + definition.values.length, 0)

  return (
    <div className={cn('lg:hidden', className)}>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger
          data-testid="mobile-facet-trigger"
          render={
            <Button variant="secondary" className="w-full justify-center sm:w-auto">
              <SlidersHorizontal className="size-4" />
              {intl.formatMessage({id: 'catalog.showFilters', defaultMessage: 'Filters'})}
              {activeCount > 0 ? (
                <span
                  className="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white"
                  aria-label={intl.formatMessage(
                    {id: 'catalog.activeFilterCount', defaultMessage: '{n} active'},
                    {n: activeCount},
                  )}
                >
                  {activeCount}
                </span>
              ) : null}
            </Button>
          }
        />
        {open ? (
          <SheetContent
            data-testid="mobile-facet-panel"
            side="left"
            className="w-[85vw] max-w-sm overflow-y-auto bg-white"
          >
            <SheetHeader className="pb-0">
              {/* The sidebar renders its own visible "Filtering" heading; this
                  labels the dialog for assistive tech without duplicating it. */}
              <SheetTitle className="sr-only">
                {intl.formatMessage({id: 'catalog.showFilters', defaultMessage: 'Filters'})}
              </SheetTitle>
            </SheetHeader>
            <div className="px-4 pb-6">
              <ProductFacetSidebar
                definitions={definitions}
                collapsedSections={collapsedSections}
                onToggleSection={onToggleSection}
                onToggleOption={onToggleOption}
                onClearAll={onClearAll}
              />
            </div>
          </SheetContent>
        ) : null}
      </Sheet>
    </div>
  )
}
